import { BookOpenCheck, Droplets, ShieldCheck } from "lucide-react";
import Booking from "./Booking";
import ServicePageFaqSection from "./ServicePageFaqSection";
import { useFaqPageJsonLd } from "@/hooks/useFaqPageJsonLd";

const OIL_BRAND_IMAGES = {
  Castrol: "/resources/oil-brand-castrol.svg",
  Valvoline: "/resources/oil-brand-valvoline.svg",
  Repsol: "/resources/oil-brand-repsol.svg",
  Mobil: "/resources/oil-brand-mobil.svg",
  "LIQUI MOLY": "/resources/oil-brand-liqui-moly.svg",
};

export default function OilsPage({ t }) {
  useFaqPageJsonLd(t.oilsPage.faq, "service-oils-faq");

  return (
    <article
      id="oils-page"
      data-testid="oils-page"
      className="bg-white border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <header>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
            {t.oilsPage.eyebrow}
          </div>
          <h1 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {t.oilsPage.title}
          </h1>
          <p className="mt-6 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
            {t.oilsPage.lead}
          </p>
          {t.oilsPage.secondary ? (
            <p className="mt-5 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
              {t.oilsPage.secondary}
            </p>
          ) : null}
        </header>

        <div className="mt-8 border border-[#e2e6ec] bg-white overflow-hidden">
          <img
            src="/resources/oils-hero.png"
            alt={t.oilsPage.heroImageAlt}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>

        <div className="mt-8 border border-[#e2e6ec] bg-[#f7f8fa] p-6">
          <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
            {t.oilsPage.standardsTitle}
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-4xl">
            {t.oilsPage.standardsLead}
          </p>

          <div className="mt-5 border border-[#e2e6ec] bg-white overflow-hidden">
            <img
              src="/resources/oils-standards-sae.png"
              alt={t.oilsPage.standardsImageAlt}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
          <div className="mt-4 border border-[#e2e6ec] bg-white overflow-hidden">
            <img
              src="/resources/oils-standards-api.png"
              alt={t.oilsPage.apiImageAlt}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-[#e2e6ec] bg-white p-5">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#0b378c]">
                {t.oilsPage.saeTitle}
              </h3>
              <ul className="mt-3 space-y-2">
                {t.oilsPage.saePoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-slate-600 leading-relaxed flex items-start gap-2">
                    <span className="text-[#0b378c] mt-1" aria-hidden="true">
                      •
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#e2e6ec] bg-white p-5">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#0b378c]">
                {t.oilsPage.apiTitle}
              </h3>
              <ul className="mt-3 space-y-2">
                {t.oilsPage.apiPoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-slate-600 leading-relaxed flex items-start gap-2">
                    <span className="text-[#0b378c] mt-1" aria-hidden="true">
                      •
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#e2e6ec] bg-white p-5">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#0b378c]">
                {t.oilsPage.aceaTitle}
              </h3>
              <ul className="mt-3 space-y-2">
                {t.oilsPage.aceaPoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-slate-600 leading-relaxed flex items-start gap-2">
                    <span className="text-[#0b378c] mt-1" aria-hidden="true">
                      •
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 border border-[#e2e6ec] bg-[#f7f8fa] p-6">
          <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
            {t.oilsPage.selectorTitle}
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
            {t.oilsPage.selectorLead}
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {t.oilsPage.selectorLinks.map((item) => (
              <a
                key={item.url}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-between gap-3 border border-[#e2e6ec] bg-white px-3 py-3 text-sm text-slate-700 hover:border-[#0b378c] hover:text-[#0b378c] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={OIL_BRAND_IMAGES[item.name]}
                    alt={`${item.name} logo`}
                    className="w-10 h-10 object-contain border border-[#e2e6ec] bg-white p-1"
                    loading="lazy"
                  />
                  <span className="font-mono uppercase tracking-[0.14em] text-xs truncate">{item.name}</span>
                </div>
                <span className="text-[#e3191b]" aria-hidden="true">
                  ↗
                </span>
              </a>
            ))}
          </div>
        </div>

        <section className="mt-12" aria-labelledby="oils-cards-heading">
          <h2
            id="oils-cards-heading"
            className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-2xl sm:text-3xl mb-8 max-w-4xl leading-tight"
          >
            {t.oilsPage.cardsSectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <BookOpenCheck className="w-4 h-4" />
                {t.oilsPage.card1Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.oilsPage.card1Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <ShieldCheck className="w-4 h-4" />
                {t.oilsPage.card2Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.oilsPage.card2Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <Droplets className="w-4 h-4" />
                {t.oilsPage.card3Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.oilsPage.card3Text}</p>
            </div>
          </div>
        </section>

        <ServicePageFaqSection
          headingId="oils-faq-heading"
          title={t.oilsPage.faqTitle}
          faq={t.oilsPage.faq}
        />

        <div className="mt-12">
          <Booking
            t={t}
            initialService={t.services.items[6]?.title || ""}
            copyOverrides={t.oilsPage.bookingCopy}
          />
        </div>
      </div>
    </article>
  );
}
